import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { allowedNodeEnvironmentFlags } from 'process';
import { NEVER, of, Subject } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, skipWhile, take, takeUntil, tap } from 'rxjs/operators';
import { fadeIn } from 'src/app/animations';
import { UserPlatformType } from 'src/app/models/enum/user-platform-type.enum';
import { AuthService } from 'src/app/services/auth.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { NavigationActionButton } from '../../navigation-bar/models/navigation-action-button.model';
import { ManagedDocuments } from 'src/app/services/ui/models/managed-documents.model';

@Component({
	selector: 'app-login-callback',
	templateUrl: './login-callback.component.html',
	styleUrls: ['./login-callback.component.scss', '../../stylesheets/job-main.scss', '../../stylesheets/job-inputs.scss'],
	animations: [fadeIn(200)]
})
export class LoginCallbackComponent implements OnInit, OnDestroy {

	public errorTitle = null;
	public errorMessage = null;

	constructor(
		private activatedRoute: ActivatedRoute,
		private authService: AuthService,
		private userAuthService: UserAuthService,
		private router: Router,
		private jwtHelper: JwtHelperService,
		private domSanitizer: DomSanitizer
	) { }

	ngOnInit(): void {
		GlobalSettingsService.priorityNavigationBarActions = new Map<string, NavigationActionButton>();

		const initialEmail = sessionStorage.getItem('initial_email');

		if (/access_token|id_token|error/.test(window.location.hash)) {

			const localStoragePlatformTypeId = parseInt(localStorage.getItem('platform_type_id'), 10);
			const platformTypeId = isNaN(localStoragePlatformTypeId) ? null : localStoragePlatformTypeId;
			const managedDocumentUrl = sessionStorage.getItem("managed-docs-url");
			const managedDocsInfoData = sessionStorage.getItem("managed-docs-info");

			sessionStorage.removeItem("managed-docs-url");
			sessionStorage.removeItem("managed-docs-info");

			this.authService.handleAuth0Token()
				.pipe(
					take(1),
					mergeMap((result: boolean) => {
						if (!result) {
							GlobalSettingsService.isGlobalLoaderLoading = false;
							GlobalSettingsService.isSecondaryLoaderLoading = false;
							this.errorMessage = `Authentication failed. Please try again!`;
							console.error('Auth0 token handling failure');
							return NEVER;
						} else {
							if(!!managedDocumentUrl) return of(result);

							const token = this.jwtHelper.decodeToken(localStorage.getItem(AuthService.Auth0IdTokenKey));

							if (!!initialEmail && initialEmail.toLowerCase() !== token.email?.toLowerCase()) {
								GlobalSettingsService.isGlobalLoaderLoading = false;
								GlobalSettingsService.isSecondaryLoaderLoading = false;
								this.errorTitle = `Invalid login email`;
								this.errorMessage = `Please use the initial email address <strong>${initialEmail}</strong>!`;
								return NEVER;
							}

							return of(result);
						}
					}
					),
					mergeMap((result: boolean) => {
						if(!!result) {							
							if(!!managedDocumentUrl && !!managedDocsInfoData){																						
								const managedDocsInfo = <{docId: number, docOid: string}>JSON.parse(managedDocsInfoData);
								return this.userAuthService.getManagedDocumentsUserAccess(managedDocsInfo.docId, managedDocsInfo.docOid)
									.pipe(
										take(1),
										mergeMap((userAccess: ManagedDocuments) => {
											if(!!userAccess && !!userAccess.accountId && !!userAccess.userAccountId){
												return this.authService.handleVtrTokenGeneration(userAccess.platformTypeId, userAccess.accountId, userAccess.userAccountId)
											}
											else {
												return this.authService.handleVtrTokenGeneration(platformTypeId);
											}
										}),
										catchError((error) => {
											if(error.status === 401){
												this.authService.removeAuth0Token();
												location.href = managedDocumentUrl;												
											}
											return NEVER;
										})
									);								
							}
							return this.authService.handleVtrTokenGeneration(platformTypeId)
						} 
						else {
							of(null)
						}
					}),
					catchError(() => {
						this.errorMessage = `Authentication failed due to an internal error. Please try again!`;
						console.error('VTR token handling failure!');
						return of(false);
					})
				).subscribe(isSuccess => {
					if (isSuccess) {
						if(!!managedDocumentUrl){
							location.href = managedDocumentUrl;
							return;
						}
						
						const returnUrl = localStorage.getItem('rurl');

						if (!!returnUrl) {
							location.href = returnUrl;
						} else {
							this.router.navigate(['/']);
						}

						sessionStorage.removeItem('initial_email');
						localStorage.removeItem('platform_type_id');
					}
				});
		} else {
			this.router.navigate(['/login']);
		}
	}

	ngOnDestroy(): void {
		GlobalSettingsService.priorityNavigationBarActions = null;
	}

	public redirectToLogin(): void {
		this.authService.login();
	}
}
